import React, { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, Palette, Ruler, Crown, Loader2, Boxes, DollarSign } from 'lucide-react';
import { fetchAdminAnalytics } from '../lib/api';

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const shortDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return iso; }
};

const RANGES = [
  { label: '7D', value: 7 },
  { label: '30D', value: 30 },
  { label: '90D', value: 90 },
  { label: 'All', value: 0 },
];

const MiniStat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-[#141311] border border-[#c6a15b]/20 p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <p className="overline text-[0.55rem] text-[#9c9587]">{label}</p>
      <Icon size={16} className="text-[#c6a15b]" />
    </div>
    <p className="font-display text-2xl text-[#f7f3ec]">{value}</p>
  </div>
);

const RankList = ({ icon: Icon, title, items, valueFn, subFn, testid }) => (
  <div className="bg-[#141311] border border-[#c6a15b]/20 p-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-5">
      <Icon size={18} className="text-[#c6a15b]" />
      <h3 className="font-display text-xl text-[#f7f3ec]">{title}</h3>
    </div>
    {items.length === 0 ? (
      <p className="font-body text-sm text-[#9c9587]">No paid orders yet.</p>
    ) : (
      <div className="space-y-3">
        {items.map((it, i) => {
          const max = valueFn(items[0]) || 1;
          const pct = Math.max((valueFn(it) / max) * 100, 6);
          return (
            <div key={it.name + i}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-body text-sm text-[#f7f3ec] truncate pr-3">
                  <span className="text-[#c6a15b] mr-2">{i + 1}</span>{it.name}
                </span>
                <span className="font-body text-xs text-[#e7d3a1] whitespace-nowrap">{subFn(it)}</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#a8823c,#e7d3a1,#c6a15b)' }} />
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  useEffect(() => {
    setLoading(true);
    fetchAdminAnalytics(range).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [range]);

  const series = data?.revenue_series || [];
  const maxRev = Math.max(1, ...series.map((s) => s.revenue_cents || 0));

  return (
    <div className="mb-10" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-2">
          <BarChart3 size={20} className="text-[#c6a15b]" />
          <h2 className="font-display text-3xl text-[#f7f3ec]">Analytics</h2>
        </div>
        <div className="flex items-center gap-1 border border-[#c6a15b]/30 rounded-full p-1" data-testid="analytics-range-filter">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              data-testid={`analytics-range-${r.value}`}
              className={`px-4 py-1.5 rounded-full font-body text-xs transition-colors ${range === r.value ? 'bg-[#c6a15b] text-[#0b0b0b] font-medium' : 'text-[#e7d3a1] hover:bg-white/5'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="bg-[#141311] border border-[#c6a15b]/20 p-10 flex items-center justify-center">
          <Loader2 size={24} className="text-[#c6a15b] animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary stats for the selected range */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <MiniStat icon={Boxes} label="Units Sold" value={data.total_units || 0} testid="analytics-total-units" />
            <MiniStat icon={DollarSign} label="Revenue" value={money(data.total_revenue_cents)} testid="analytics-total-revenue" />
            <MiniStat icon={Crown} label="Paid Orders" value={data.paid_orders || 0} testid="analytics-paid-orders" />
          </div>

          {/* Revenue trend */}
          <div className="bg-[#141311] border border-[#c6a15b]/20 p-6 mb-6" data-testid="analytics-revenue-trend">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp size={18} className="text-[#c6a15b]" />
              <h3 className="font-display text-xl text-[#f7f3ec]">Revenue Trend</h3>
              <span className="font-body text-xs text-[#9c9587] ml-1">· {series.length} day{series.length === 1 ? '' : 's'} with sales</span>
            </div>
            {series.length === 0 ? (
              <p className="font-body text-sm text-[#9c9587]">No paid orders in this range — revenue will chart here as sales come in.</p>
            ) : (
              <div className="flex items-end gap-2 h-44 overflow-x-auto pb-1">
                {series.map((s) => (
                  <div key={s.date} className="flex flex-col items-center justify-end gap-2 min-w-[34px] flex-1 group">
                    <span className="font-body text-[0.6rem] text-[#e7d3a1] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">{money(s.revenue_cents)}</span>
                    <div
                      className="w-full rounded-t transition-all duration-500"
                      style={{ height: `${Math.max((s.revenue_cents / maxRev) * 140, 4)}px`, background: 'linear-gradient(180deg,#e7d3a1,#c6a15b,#a8823c)' }}
                      title={`${money(s.revenue_cents)} · ${s.orders} order${s.orders === 1 ? '' : 's'}`}
                    />
                    <span className="font-body text-[0.55rem] text-[#9c9587] whitespace-nowrap">{shortDate(s.date)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Best-sellers */}
          <div className="grid md:grid-cols-3 gap-6">
            <RankList
              icon={Palette} title="Top Shades" testid="analytics-top-shades"
              items={data.top_shades || []}
              valueFn={(x) => x.qty}
              subFn={(x) => `${x.qty} sold · ${money(x.revenue_cents)}`}
            />
            <RankList
              icon={Ruler} title="Top Lengths" testid="analytics-top-lengths"
              items={data.top_lengths || []}
              valueFn={(x) => x.qty}
              subFn={(x) => `${x.qty} sold`}
            />
            <RankList
              icon={Crown} title="Best Sellers" testid="analytics-top-products"
              items={data.top_products || []}
              valueFn={(x) => x.qty}
              subFn={(x) => `${x.qty} sold · ${money(x.revenue_cents)}`}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
